import { useState, useRef, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { debounce } from 'lodash';
import { 
  isHebrewText, 
  hebrewPhoneticNormalize, 
  areHebrewWordsPhoneticallySimilar 
} from './hebrewPhoneticUtils';

// Extract policyId from an index document ID, supports chunks too
const extractPolicyIdFromDocId = (id) => {
  // Matches: ..._policy_<POLICY> or ..._policy_<POLICY>_chunk_<n>
  const m = id.match(/_policy_([^_]+)(?:$|_chunk_\d+$)/);
  return m ? m[1] : null;
};


// Build inverted index structure for fast searching
const buildInvertedIndex = (cache) => {
  const index = {
    byFirstChar: new Map(),
    byFirstTwo: new Map(),
    byTableColumn: new Map(), // Keep track of which table/column each value belongs to
    byWordStart: new Map() // NEW: Index by starting characters of EACH word
  };
  
  cache.forEach((data, docId) => {
    data.values.forEach((item, itemIndex) => {
      if (!item.value) return;
      
      const valueLower = item.valueLower || item.value.toLowerCase(); // Use pre-computed if available
      const entry = {
        docId,
        value: item.value,
        valueLower,
        tableId: data.tableId,
        columnName: data.columnName,
        tableName: data.tableName,
        itemIndex
      };
      
      // Index by first character of the whole value
      const firstChar = valueLower[0];
      if (!index.byFirstChar.has(firstChar)) {
        index.byFirstChar.set(firstChar, []);
      }
      index.byFirstChar.get(firstChar).push(entry);
      
      // Index by first two characters of the whole value
      if (valueLower.length >= 2) {
        const firstTwo = valueLower.substring(0, 2);
        if (!index.byFirstTwo.has(firstTwo)) {
          index.byFirstTwo.set(firstTwo, []);
        }
        index.byFirstTwo.get(firstTwo).push(entry);
      }
      
      // NEW: Index by first character of EACH WORD in the value
      const words = valueLower.split(/\s+/);
      words.forEach(word => {
        if (word.length > 0) {
          const wordFirstChar = word[0];
          if (!index.byWordStart.has(wordFirstChar)) {
            index.byWordStart.set(wordFirstChar, []);
          }
          // Only add if not already in the list for this character
          const existingEntries = index.byWordStart.get(wordFirstChar);
          const alreadyExists = existingEntries.some(e => 
            e.value === entry.value && e.tableId === entry.tableId && e.columnName === entry.columnName
          );
          if (!alreadyExists) {
            index.byWordStart.get(wordFirstChar).push(entry);
          }
          
          // Also index by first two characters of each word
          if (word.length >= 2) {
            const wordFirstTwo = word.substring(0, 2);
            if (!index.byWordStart.has(wordFirstTwo)) {
              index.byWordStart.set(wordFirstTwo, []);
            }
            const existingEntriesTwo = index.byWordStart.get(wordFirstTwo);
            const alreadyExistsTwo = existingEntriesTwo.some(e => 
              e.value === entry.value && e.tableId === entry.tableId && e.columnName === entry.columnName
            );
            if (!alreadyExistsTwo) {
              index.byWordStart.get(wordFirstTwo).push(entry);
            }
          }
        }
      });
      
      // Index by table-column combination
      const tableColumnKey = `${data.tableId}_${data.columnName}`;
      if (!index.byTableColumn.has(tableColumnKey)) {
        index.byTableColumn.set(tableColumnKey, []);
      }
      index.byTableColumn.get(tableColumnKey).push(entry);
    });
  });
  
  return index;
};

// Get relevant candidates for search based on inverted index
const getCandidatesForSearch = (searchTerm, invertedIndex, searchColumnId = null) => {
  // If searching specific column, return only those values
  if (searchColumnId && invertedIndex.byTableColumn.has(searchColumnId)) {
    return invertedIndex.byTableColumn.get(searchColumnId);
  }
  
  const searchLower = searchTerm.toLowerCase();
  const candidates = new Set();
  
  // Add candidates starting with same first character (whole value)
  const firstChar = searchLower[0];
  const firstCharCandidates = invertedIndex.byFirstChar.get(firstChar) || [];
  firstCharCandidates.forEach(entry => candidates.add(entry));
  
  // NEW: Also check the word-start index for values where ANY word starts with this character
  const wordStartCandidates = invertedIndex.byWordStart.get(firstChar) || [];
  wordStartCandidates.forEach(entry => candidates.add(entry));
  
  // For short searches (2-4 chars), include candidates with same first two chars
  if (searchLower.length >= 2 && searchLower.length <= 4) {
    const firstTwo = searchLower.substring(0, 2);
    const firstTwoCandidates = invertedIndex.byFirstTwo.get(firstTwo) || [];
    firstTwoCandidates.forEach(entry => candidates.add(entry));
    
    // NEW: Also check word-start index for two characters
    const wordStartTwoCandidates = invertedIndex.byWordStart.get(firstTwo) || [];
    wordStartTwoCandidates.forEach(entry => candidates.add(entry));
  }
  
  // For Hebrew, add phonetically similar first characters
  if (isHebrewText(searchTerm)) {
    const similarChars = {
      'א': ['ע'],
      'ע': ['א'],
      'כ': ['ק'],
      'ק': ['כ'],
      'ב': ['ו'],
      'ו': ['ב'],
      'ת': ['ט'],
      'ט': ['ת'],
      'ס': ['צ', 'ש'],
      'צ': ['ס', 'ז'],
      'ש': ['ס']
    };
    
    const similar = similarChars[firstChar] || [];
    similar.forEach(char => {
      const simCandidates = invertedIndex.byFirstChar.get(char) || [];
      simCandidates.forEach(entry => candidates.add(entry));
      
      // NEW: Also check word-start index for similar Hebrew characters
      const simWordCandidates = invertedIndex.byWordStart.get(char) || [];
      simWordCandidates.forEach(entry => candidates.add(entry));
    });
  }
  
  return Array.from(candidates);
};

// Levenshtein distance implementation to measure string similarity
const levenshteinDistance = (str1, str2) => {
  const track = Array(str2.length + 1).fill(null).map(() => 
    Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }
  
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }
  
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator, // substitution
      );
    }
  }
  
  return track[str2.length][str1.length];
};

// Format helper functions
const formatTableName = (name) => {
  if (!name) return '';
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatColumnName = (name) => {
  if (!name) return '';
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Custom hook for index search functionality
const useIndexSearch = (options = {}) => {
const {
  minChars = 3,
  maxResults = 10,
  cacheTTL = 15 * 60 * 1000,
  cachePrefix = 'indexSearch',
  shouldSortResults = true,
  allowedTables = null,
  debounceTime = 300,
  userAccessConfig = null,
  policyId = null // kept for API compatibility; ignored in wrapper mode
} = options;

// Wrapper mode: no single/primary policy, and no base fallback
const isWrapperAccess = userAccessConfig?.accessMethod === 'wrapper';
const userPolicyIds = Array.isArray(userAccessConfig?.policyIds) ? userAccessConfig.policyIds : [];

  
  const [indexCache, setIndexCache] = useState(null);
  const [invertedIndex, setInvertedIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const allowedTablesRef = useRef(allowedTables);
  const userAccessConfigRef = useRef(userAccessConfig);
  const commonWords = new Set([
    'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i', 'it', 'for',
    'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at', 'this', 'but', 'his',
    'by', 'from', 'they', 'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my',
    'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out', 'if',
    'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make', 'can', 'like',
    'time', 'no', 'just', 'him', 'know', 'take', 'people', 'into', 'year', 'your',
    'good', 'some', 'could', 'them', 'see', 'other', 'than', 'then', 'now', 'look',
    'only', 'come', 'its', 'over', 'think', 'also', 'back', 'after', 'use', 'two',
    'how', 'our', 'work', 'first', 'well', 'way', 'even', 'new', 'want', 'because',
    'any', 'these', 'give', 'day', 'most', 'us', 'compare'
  ]);

  // Update ref when allowedTables changes
  useEffect(() => {
    if (allowedTables && JSON.stringify(allowedTables) !== JSON.stringify(allowedTablesRef.current)) {
      allowedTablesRef.current = allowedTables;
      // Clear the cache when allowedTables changes
      setIndexCache(null);
    }
  }, [allowedTables]);

  // Update ref when userAccessConfig changes
  useEffect(() => {
    if (userAccessConfig && JSON.stringify(userAccessConfig) !== JSON.stringify(userAccessConfigRef.current)) {
      userAccessConfigRef.current = userAccessConfig;
      // Clear the cache when access config changes
      setIndexCache(null);
      setInvertedIndex(null);
    }
  }, [userAccessConfig]);

  // Cache management functions
  const saveToCache = useCallback((key, data) => {
    try {
      const cacheKey = `${cachePrefix}_${key}`;
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(`${cacheKey}_expiry`, String(Date.now() + cacheTTL));
    } catch (error) {
    }
  }, [cachePrefix, cacheTTL]);

  const getFromCache = useCallback((key) => {
    try {
      const cacheKey = `${cachePrefix}_${key}`;
      const expiryTime = parseInt(localStorage.getItem(`${cacheKey}_expiry`) || '0', 10);
      if (Date.now() > expiryTime) {
        return null; // Cache expired
      }
      
      const cachedData = localStorage.getItem(cacheKey);
      return cachedData ? JSON.parse(cachedData) : null;
    } catch (error) {
      return null;
    }
  }, [cachePrefix]);

   // Fetch all indexed columns (policy-aware)
   const fetchIndexedColumns = useCallback(async (useCache = true) => {
    if (!allowedTablesRef.current) {
      return [];
    }

    const cacheKey = 'indexedColumns';
    try {
      setLoading(true);
      setError(null);
      
      if (useCache) {
        const cachedColumns = getFromCache(cacheKey);
        if (cachedColumns) {
          return cachedColumns;
        }
      }
      
      const configDoc = await getDoc(doc(db, 'appConfiguration', 'tables'));
      if (!configDoc.exists()) {
        return [];
      }

      const docData = configDoc.data();
      const tablesData = Object.entries(docData)
        .filter(([name, value]) => 
          value && 
          typeof value === 'object' && 
          value.schema && 
          Array.isArray(value.schema) &&
          value.id &&
          (allowedTablesRef.current[value.id] || allowedTablesRef.current[name])
        )
        .map(([name, tableData]) => ({
          id: tableData.id || name,
          name: name,
          displayName: formatTableName(name),
          schema: (tableData.schema || []).filter(field => 
            field && 
            field.name &&
            !field.name.toLowerCase().includes('created') &&
            !field.name.toLowerCase().includes('modified') &&
            !field.name.toLowerCase().includes('timestamp') &&
            field.mode !== 'REPEATED'
          )
        }));

        const indexesSnapshot = await getDocs(collection(db, 'index'));
        const indexMap = new Map();
        
        const currentAccessConfig = userAccessConfigRef.current;
        const wrapper = currentAccessConfig?.accessMethod === 'wrapper';
        const allPolicyIds = Array.isArray(currentAccessConfig?.policyIds) ? currentAccessConfig.policyIds : [];
        
        indexesSnapshot.forEach(d => {
          const data = d.data();
          if (!data?.tableId || !data?.columnName) return;
        
          const table = tablesData.find(t => t.id === data.tableId);
          if (!table) return;
        
          const docPolicyId = data.policyId || extractPolicyIdFromDocId(d.id);
        
          if (wrapper) {
            // Wrapper: include ONLY policy docs that match ANY of the user's policies (no base)
            if (!docPolicyId || !allPolicyIds.includes(docPolicyId)) return;
          } else {
            // View-based: include ONLY base docs
            if (docPolicyId) return;
          }
        
          const columnSchema = table.schema.find(col => col.name === data.columnName);
          if (!columnSchema) return;
        
          const key = `${table.id}_${data.columnName}`;
          // Just record that this column is indexed (dedup via key)
          if (!indexMap.has(key)) {
            indexMap.set(key, {
              tableId: table.id,
              tableName: table.name,
              tableDisplayName: table.displayName,
              columnName: data.columnName,
              columnDisplayName: formatColumnName(data.columnName),
              type: columnSchema.type || 'STRING'
            });
          }
        });        
      
      const columnsArray = Array.from(indexMap.values());
      saveToCache(cacheKey, columnsArray);
      return columnsArray;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getFromCache, saveToCache]);


  // Get cached index data or fetch it
  const getIndexCache = useCallback(async () => {
    if (indexCache && invertedIndex) return { cache: indexCache, index: invertedIndex };
    if (!allowedTablesRef.current) {
      return { cache: new Map(), index: null };
    }
  
    setLoading(true);
    try {
      const indexCollection = collection(db, 'index');
      const snapshot = await getDocs(indexCollection);
      const cache = new Map();
      const multiChunkIndexes = new Map();
      const chunkDocuments = new Map();
  
      const currentAccessConfig = userAccessConfigRef.current;
      const wrapper = currentAccessConfig?.accessMethod === 'wrapper';
      const allPolicyIds = Array.isArray(currentAccessConfig?.policyIds) ? currentAccessConfig.policyIds : [];
  
      // First pass: include only allowed docs with policy filtering
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (!data?.tableId) return;
  
        const simpleTableName = data.tableId.split('.').pop();
        if (!allowedTablesRef.current[data.tableId] && !allowedTablesRef.current[simpleTableName]) {
          return;
        }
  
        const docPolicyId = data.policyId || extractPolicyIdFromDocId(d.id);
  
        if (wrapper) {
          // Wrapper: include ONLY policy docs that match ANY of the user's policies (no base)
          if (!docPolicyId || !allPolicyIds.includes(docPolicyId)) return;
        } else {
          // View-based: include ONLY base docs
          if (docPolicyId) return;
        }
  
        const tableName = data.tableId.split('.').slice(-1)[0]
          .split('_')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
  
        // Chunk doc
        if (data.isChunk && data.parentDocId) {
          if (!chunkDocuments.has(data.parentDocId)) {
            chunkDocuments.set(data.parentDocId, []);
          }
          chunkDocuments.get(data.parentDocId).push({
            docId: d.id,
            chunkIndex: data.chunkIndex,
            values: data.values || []
          });
          return;
        }
  
        // Multi-chunk main
        if (data.isMultiChunk) {
          multiChunkIndexes.set(d.id, {
            tableId: data.tableId,
            columnName: data.columnName,
            tableName,
            chunks: data.chunks,
            values: []
          });
          return;
        }
  
        // Regular single-doc index
        if (Array.isArray(data.values)) {
          const processedValues = data.values.map(item => ({
            ...item,
            valueLower: item.value ? item.value.toLowerCase() : ''
          }));
  
          cache.set(d.id, {
            values: processedValues,
            tableId: data.tableId,
            columnName: data.columnName,
            tableName
          });
        }
      });
  
      // Merge chunks for retained multi-chunk indexes
      multiChunkIndexes.forEach((indexData, docId) => {
        const chunks = chunkDocuments.get(docId) || [];
        chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
        let allValues = [];
        chunks.forEach(chunk => {
          if (Array.isArray(chunk.values)) allValues = [...allValues, ...chunk.values];
        });
        if (allValues.length > 0) {
          indexData.values = allValues.map(item => ({
            ...item,
            valueLower: item.value ? item.value.toLowerCase() : ''
          }));
          cache.set(docId, indexData);
        }
      });
  
      const index = buildInvertedIndex(cache);
      setIndexCache(cache);
      setInvertedIndex(index);
  
      if (typeof window !== 'undefined' && localStorage.getItem('INDEX_DEBUG') === '1') {
        const rows = [];
        cache.forEach((entry, docId) => {
          const m = /_policy_([^_]+)(?:$|_chunk_\d+$)/.exec(docId);
          rows.push({
            docId,
            tableId: entry.tableId,
            simpleTable: entry.tableId ? entry.tableId.split('.').pop() : null,
            columnName: entry.columnName || null,
            policyId: m ? m[1] : (entry.policyId || null),
            valuesCount: Array.isArray(entry.values) ? entry.values.length : 0
          });
        });
        console.groupCollapsed('%c[AC] Included index docs', 'color:#6B5B95;font-weight:bold;');
        console.table(rows);
        console.groupEnd();
      }
  
      return { cache, index };
  
    } catch (err) {
      setError(err);
      return { cache: new Map(), index: null };
    } finally {
      setLoading(false);
    }
  }, [indexCache, invertedIndex]);
  


  // Fetch values for a specific column
  const fetchColumnValues = useCallback(async (column) => {
    if (!column || !column.tableId || !column.columnName) {
      return [];
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const safeTableId = column.tableId.replace(/\./g, '_');
  
      if (isWrapperAccess) {
        // Wrapper: union values from ALL user policies; never read base
        const policies = Array.isArray(userPolicyIds) ? userPolicyIds : [];
        if (policies.length === 0) return [];
  
        let allValues = [];
        for (const pid of policies) {
          const policyDocId = `${safeTableId}_${column.columnName}_policy_${pid}`;
          const snap = await getDoc(doc(db, 'index', policyDocId));
          if (!snap.exists()) continue;
  
          const data = snap.data();
          if (data?.isMultiChunk && data.chunks > 0) {
            for (let i = 1; i <= data.chunks; i++) {
              const chunkSnap = await getDoc(doc(db, 'index', `${policyDocId}_chunk_${i}`));
              if (chunkSnap.exists() && Array.isArray(chunkSnap.data()?.values)) {
                allValues = allValues.concat(chunkSnap.data().values);
              }
            }
          } else if (Array.isArray(data?.values)) {
            allValues = allValues.concat(data.values);
          }
        }
  
        const unique = Array.from(new Set(allValues.map(v => v.value)));
        return unique.map(value => ({ value }));
      }
  
      // View-based: base doc only
      const baseDocId = `${safeTableId}_${column.columnName}`;
      const baseSnap = await getDoc(doc(db, 'index', baseDocId));
      if (!baseSnap.exists()) return [];
  
      const baseData = baseSnap.data();
      if (baseData?.isMultiChunk && baseData.chunks > 0) {
        let allValues = [];
        for (let i = 1; i <= baseData.chunks; i++) {
          const chunkSnap = await getDoc(doc(db, 'index', `${baseDocId}_chunk_${i}`));
          if (chunkSnap.exists() && Array.isArray(chunkSnap.data()?.values)) {
            allValues = allValues.concat(chunkSnap.data().values);
          }
        }
        const unique = Array.from(new Set(allValues.map(v => v.value)));
        return unique.map(value => ({ value }));
      }
  
      if (Array.isArray(baseData?.values)) {
        const unique = Array.from(new Set(baseData.values.map(v => v.value)));
        return unique.map(value => ({ value }));
      }
  
      return [];
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [isWrapperAccess, userPolicyIds]);
  

  // Search for values across all indexes
  const searchValues = useCallback(async (searchTerm, options = {}) => {
    const {
      previousWord = '',
      currentWord = searchTerm,
      currentWordStart = 0,
      currentWordEnd = currentWord.length,
      combinedWords = previousWord ? `${previousWord} ${currentWord}` : currentWord,
      searchColumnId = null, // If provided, only search this specific column
      exactMatchOnly = false, // If true, skip fuzzy matching
      skipQuotedCheck = false, // If true, skip checking for quoted values in input
      inputValue = '',  // Full input for quoted check
      lastSelectedPosition = { start: -1, end: -1, text: '' },
      fullInputSearch = null, // Full input for multi-word search
    } = options;

    // Early return conditions
    if (!currentWord || 
        (previousWord ? currentWord.length < 2 : currentWord.length < minChars)) {
      return { results: [], matchType: 'none' };
    }
    
    // Check if current position matches last selected position
    if (lastSelectedPosition.start !== -1 &&
        currentWordStart === lastSelectedPosition.start && 
        currentWordEnd === lastSelectedPosition.end &&
        currentWord === lastSelectedPosition.text) {
      return { results: [], matchType: 'lastSelected' };
    }
    
    // Skip if the search term is in the last selected text
    if (lastSelectedPosition.text && 
        lastSelectedPosition.text.toLowerCase().includes(currentWord.toLowerCase())) {
      return { results: [], matchType: 'withinLastSelected' };
    }

    // Check if value is within quotes (for normal search mode)
    if (!skipQuotedCheck && inputValue) {
      // Find all quoted segments in the entire input
      const quotedSegments = [];
      const quoteRegex = /"([^"]*)"/g;
      let match;
      while ((match = quoteRegex.exec(inputValue)) !== null) {
        quotedSegments.push({
          start: match.index,
          end: match.index + match[0].length,
          value: match[1]
        });
      }
      
      // Check if current word position overlaps with any quoted segment
      const isWithinQuotes = quotedSegments.some(segment => 
        (currentWordStart >= segment.start && currentWordStart < segment.end) || 
        (currentWordEnd > segment.start && currentWordEnd <= segment.end) ||
        (currentWordStart <= segment.start && currentWordEnd >= segment.end)
      );
      
      // If the current word is within quotes, don't show suggestions
      if (isWithinQuotes) {
        return { results: [], matchType: 'withinQuotes' };
      }
    }

    // NEW: Get cache and inverted index
    const { cache, index } = await getIndexCache();
    
    // If no index available, return empty
    if (!index) {
      return { results: [], matchType: 'none' };
    }

    const results = [];
    const uniqueValues = new Map();
    const wordOrderMatches = new Map();
    const maxWordOrderMatches = 5;

    // Create search variations
    const currentWordLower = currentWord.toLowerCase();
    const previousWordLower = previousWord ? previousWord.toLowerCase() : '';
    const combinedWordsLower = combinedWords.toLowerCase();
    
    // Check if we're doing a multi-word search
    const fullInputLower = fullInputSearch ? fullInputSearch.toLowerCase() : '';
    const isMultiWordSearch = (currentWordLower.includes(' ') || 
                              (previousWordLower && combinedWordsLower.includes(' ')) ||
                              (fullInputLower && fullInputLower.includes(' ')));
    
    // Use full input for search words if available
    let searchWords = [];
    if (fullInputLower && fullInputLower.includes(' ')) {
      searchWords = fullInputLower.split(/\s+/).filter(w => w.length > 0);
    } else if (isMultiWordSearch) {
      searchWords = (previousWordLower ? combinedWordsLower : currentWordLower).split(/\s+/).filter(w => w.length > 0);
    }

    // Check if current word starts with 'ל' and create alternative strings
    const hasLPrefix = currentWordLower.startsWith('ל');
    const currentWithoutL = hasLPrefix ? currentWordLower.slice(1) : currentWordLower;

    // Determine search strategy based on word lengths
    const shouldSearchCurrentWord = !previousWordLower && currentWordLower.length >= minChars;
    const shouldSearchCombined = previousWordLower && currentWordLower.length >= 2;
    const shouldIncludeCurrentWord = previousWordLower && currentWordLower.length >= minChars;

    // Check if the search term is Hebrew
    const isSearchHebrew = isHebrewText(currentWord);
    const normalizedSearchTerm = isSearchHebrew ? hebrewPhoneticNormalize(currentWord) : null;

    // OPTIMIZATION: Determine if search starts with letter or number
    const searchStartsWithLetter = /^[a-zA-Z\u0590-\u05FF]/.test(currentWordLower);
    const searchStartsWithNumber = /^[0-9]/.test(currentWordLower);

    // NEW: Get candidates using inverted index
    const candidates = getCandidatesForSearch(currentWord, index, searchColumnId);
    
    // NEW: Search through candidates instead of entire cache
    candidates.forEach(entry => {
      const { docId, value, valueLower, tableId, columnName, tableName } = entry;
      
      // Skip if table is not in allowed tables
      // Extract simple table name from full ID
      const simpleTableName = tableId.split('.').pop();
      if (!allowedTablesRef.current || (!allowedTablesRef.current[tableId] && !allowedTablesRef.current[simpleTableName])) {
        return;
      }
      
      // OPTIMIZATION: Skip based on first character type (using pre-computed valueLower)
      if (searchStartsWithLetter && /^[0-9]/.test(valueLower)) return;
      if (searchStartsWithNumber && /^[a-zA-Z\u0590-\u05FF]/.test(valueLower)) return;
      
      // For multi-word searches, check word-order match FIRST
      if (isMultiWordSearch && searchWords.length > 1 && wordOrderMatches.size < maxWordOrderMatches) {
        const itemWords = valueLower.split(/\s+/).filter(w => w.length > 0);
        const hasAllWords = searchWords.every(sw => 
          itemWords.some(iw => iw === sw || iw.includes(sw))
        );
        
        if (hasAllWords && !wordOrderMatches.has(valueLower)) {
          const contextEntry = {
            value: value,
            tableName: tableName,
            tableId: tableId,
            columnName: columnName,
            isExactMatch: true,
            matchType: 'word-order'
          };
          
          wordOrderMatches.set(valueLower, {
            value: value,
            originalSearch: currentWord,
            contexts: [contextEntry],
            isExactMatch: true,
            matchType: 'word-order'
          });
          return;
        }
      }
      
      // Stop checking if we have enough matches
      if (wordOrderMatches.size >= maxWordOrderMatches && uniqueValues.size >= maxResults) {
        return;
      }
      
      const isCombinedExact = previousWordLower && valueLower.includes(combinedWordsLower);
      if (uniqueValues.size >= maxResults && !uniqueValues.has(valueLower) && !isCombinedExact) return;
      
      // Check for exact substring matching (using pre-computed valueLower)
      let isExactMatch = 
        (shouldSearchCombined && valueLower.includes(combinedWordsLower)) ||
        ((shouldSearchCurrentWord || shouldIncludeCurrentWord) && 
          (valueLower.includes(currentWordLower) || 
          (hasLPrefix && valueLower.includes(currentWithoutL))));
      
      // If exact match found, add to results
      if (isExactMatch) {
        const contextEntry = {
          value: value,
          tableName: tableName,
          tableId: tableId,
          columnName: columnName,
          isExactMatch: true,
          matchType: 'exact'
        };
        
        if (uniqueValues.has(valueLower)) {
          const existing = uniqueValues.get(valueLower);
          existing.contexts.push(contextEntry);
        } else {
          uniqueValues.set(valueLower, {
            value: value,
            originalSearch: currentWord,
            contexts: [contextEntry],
            isExactMatch: true,
            matchType: 'exact'
          });
        }
        
        return;
      }

      // Check for Hebrew phonetic matching
      if (isSearchHebrew && isHebrewText(value) && normalizedSearchTerm) {
        if (areHebrewWordsPhoneticallySimilar(currentWord, value)) {
          const contextEntry = {
            value: value,
            tableName: tableName,
            tableId: tableId,
            columnName: columnName,
            isExactMatch: false,
            matchType: 'phonetic'
          };
          
          if (uniqueValues.has(valueLower)) {
            const existing = uniqueValues.get(valueLower);
            if (existing.matchType !== 'exact') {
              existing.contexts.push(contextEntry);
              existing.matchType = 'phonetic';
            }
          } else {
            uniqueValues.set(valueLower, {
              value: value,
              originalSearch: currentWord,
              contexts: [contextEntry],
              isExactMatch: false,
              matchType: 'phonetic'
            });
          }
          return;
        }
      }
      
      // If requested exact matches only, skip fuzzy matching
      if (exactMatchOnly) {
        return;
      }
      
      // NEW: Limit fuzzy matching to first 2000 candidates for performance
      const candidateIndex = candidates.indexOf(entry);
      if (candidateIndex > 2000) {
        return;
      }
      
      // Try fuzzy matching for longer words
      if ((currentWordLower.length >= 3) && 
          ((shouldSearchCurrentWord || shouldIncludeCurrentWord))) {
        const searchTermForFuzzy = hasLPrefix ? currentWithoutL : currentWordLower;
        const threshold = Math.max(1, Math.floor(searchTermForFuzzy.length * 0.2));
        
        let distance = Infinity;
        if (valueLower.includes(' ')) {
          const words = valueLower.split(' ');
          for (const word of words) {
            if (word.length >= 2) {
              const wordDistance = levenshteinDistance(
                searchTermForFuzzy,
                word.substring(0, Math.min(word.length, searchTermForFuzzy.length + 3))
              );
              distance = Math.min(distance, wordDistance);
            }
          }
        } else {
          distance = levenshteinDistance(
            searchTermForFuzzy,
            valueLower.substring(0, Math.min(valueLower.length, searchTermForFuzzy.length + 3))
          );
        }
        
        const adjustedThreshold = currentWordLower.length <= 4 ? 1 : threshold;
        
        if (distance <= adjustedThreshold) {
          const contextEntry = {
            value: value,
            tableName: tableName,
            tableId: tableId,
            columnName: columnName,
            isExactMatch: false,
            matchType: 'fuzzy',
            distance: distance
          };
          
          if (uniqueValues.has(valueLower)) {
            const existing = uniqueValues.get(valueLower);
            if (!['exact', 'phonetic'].includes(existing.matchType)) {
              existing.contexts.push(contextEntry);
              existing.distance = distance;
            }
          } else {
            uniqueValues.set(valueLower, {
              value: value,
              originalSearch: currentWord,
              contexts: [contextEntry],
              isExactMatch: false,
              matchType: 'fuzzy',
              distance: distance
            });
          }
        }
      }
    });
    
    // Combine word-order matches with other matches
    const allMatches = new Map([...wordOrderMatches, ...uniqueValues]);
    
    // Convert Map to array for display, using only unique values
    const deduplicatedResults = Array.from(allMatches.values())
      .map(item => ({
        value: item.value,
        originalSearch: item.originalSearch,
        isExactMatch: item.isExactMatch,
        matchType: item.matchType,
        tableId: item.contexts[0].tableId,
        columnName: item.contexts[0].columnName,
        tableName: item.contexts[0].tableName,
        distance: item.matchType === 'fuzzy' ? item.contexts[0].distance : 0,
        valueContext: item.contexts
      }));
    
    if (shouldSortResults) {
      deduplicatedResults.sort((a, b) => {
        const matchTypePriority = { 'word-order': -1, 'exact': 0, 'phonetic': 1, 'fuzzy': 2 };
        const aPriority = matchTypePriority[a.matchType] || 999;
        const bPriority = matchTypePriority[b.matchType] || 999;
        
        if (aPriority !== bPriority) return aPriority - bPriority;
        
        if (a.matchType === 'exact' && b.matchType === 'exact') {
          const aStartsWithCurrent = a.value.toLowerCase().startsWith(currentWithoutL);
          const bStartsWithCurrent = b.value.toLowerCase().startsWith(currentWithoutL);
          if (aStartsWithCurrent !== bStartsWithCurrent) return bStartsWithCurrent ? 1 : -1;
        }
        
        if (a.matchType === 'fuzzy' && b.matchType === 'fuzzy') {
          if (a.distance !== b.distance) return a.distance - b.distance;
        }
        
        if (previousWordLower) {
          const aMatchesCombined = a.value.toLowerCase().includes(combinedWordsLower);
          const bMatchesCombined = b.value.toLowerCase().includes(combinedWordsLower);
          if (aMatchesCombined !== bMatchesCombined) return bMatchesCombined ? 1 : -1;
        }
        
        return a.value.localeCompare(b.value);
      });
    }
    
    return { 
      results: deduplicatedResults.slice(0, maxResults),
      matchType: deduplicatedResults.length > 0 ? deduplicatedResults[0].matchType : 'none'
    };
  }, [minChars, maxResults, getIndexCache, shouldSortResults]);

  // Debounced version of searchValues
  const debouncedSearch = useCallback(
    debounce((searchTerm, options) => searchValues(searchTerm, options), debounceTime, { leading: true }), 
    [searchValues, debounceTime]
  );

  // Convenience function to get current word from text and cursor position
  const getCurrentWord = useCallback((text, position, suggestion = null) => {
    const beforeCursor = text.slice(0, position);
    const lastSpaceIndex = beforeCursor.lastIndexOf(' ');
    const lastWord = beforeCursor.slice(lastSpaceIndex + 1);
    
    // Get the previous word if it exists
    const textBeforeLastWord = beforeCursor.slice(0, lastSpaceIndex);
    const previousSpaceIndex = textBeforeLastWord.lastIndexOf(' ');
    const previousWord = lastSpaceIndex > 0 ? textBeforeLastWord.slice(previousSpaceIndex + 1) : '';
    
    const combinedWords = previousWord ? `${previousWord} ${lastWord}` : lastWord;
    
    if (suggestion) {
      const suggestionWords = suggestion.toLowerCase().split(' ');
      const textWords = beforeCursor.toLowerCase().split(' ');
      let matchStart = textWords.length - 1;
      
      for (let i = textWords.length - 1; i >= 0; i--) {
        const word = textWords[i];
        if (suggestionWords.some(sw => sw.startsWith(word))) {
          matchStart = i;
        } else {
          break;
        }
      }
      
      const startPos = textWords.slice(0, matchStart).join(' ').length;
      return {
        word: lastWord,
        previousWord,
        combinedWords,
        start: matchStart === 0 ? 0 : startPos + 1,
        end: beforeCursor.length,
        hasLPrefix: lastWord.startsWith('ל')
      };
    }
    
    return {
      word: lastWord,
      previousWord,
      combinedWords,
      start: lastSpaceIndex + 1,
      end: beforeCursor.length,
      hasLPrefix: lastWord.startsWith('ל')
    };
  }, []);

  // Cancel debounced search on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const clearCache = useCallback(() => {
    // Clear in-memory cache
    setIndexCache(null);
    setInvertedIndex(null); // NEW: Clear inverted index too
    
    // Clear localStorage items related to this cache
    try {
      // IMPORTANT: Explicitly clear the indexedColumns cache 
      const indexedColumnsKey = `${cachePrefix}_indexedColumns`;
      localStorage.removeItem(indexedColumnsKey);
      localStorage.removeItem(`${indexedColumnsKey}_expiry`);
      
      // Also clear other cache items
      const keys = Object.keys(localStorage);
      let clearedItems = 0;
      
      keys.forEach(key => {
        if (key.startsWith(`${cachePrefix}_`)) {
          localStorage.removeItem(key);
          clearedItems++;
        }
      });
      
    } catch (error) {
    }
  }, [cachePrefix]);
  
  return {
    loading,
    error,
    searchValues,
    debouncedSearch,
    fetchIndexedColumns,
    fetchColumnValues,
    getCurrentWord,
    getIndexCache,
    clearCache
  };
};

export default useIndexSearch;